import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { PageContainer } from 'src/components/common/PageContainer';
import {
  FileText,
  ChevronDown,
  Copy,
  MapPin,
  UserPlus,
  Check,
  Archive,
  AlertCircle,
  ClockIcon,
  Inbox,
  CheckSquare,
} from 'lucide-react';
import { Button } from 'src/components/ui/button';
import { useToast } from 'src/hooks/use-toast';
import { useAuth } from 'src/hooks/useAuth';
import TablePagination from 'src/components/common/TablePagination';
import TaFilterBar from 'src/components/common/TaFilterBar';
import MetricTile from 'src/components/common/MetricTile';
import { cn, formatDate } from 'src/lib/utils';

// Lifecycle vocabulary for assignment_details.status — mirrors the tab groups
// (Active = PENDING/IN_PROGRESS/COMPLETED, Archive = ARCHIVED). There's no
// dedicated Closed tab anymore — a COMPLETED row now surfaces inside Active
// alongside the still-in-progress ones, distinguished by its own terminal
// pill (see UrgencyPill/AssignmentCard below) rather than a separate tab.
// CLOSED is kept in this map in case a row's own status still ever comes
// back as that value, even with no tab left that filters for it specifically.
// Terminal statuses stop showing a deadline-driven urgency pill: once work is
// done, the pill switches to the outcome instead.
const STATUS = {
  PENDING: {
    label: 'Pending',
    dotClass: 'bg-ds-warning',
    terminal: false,
  },
  IN_PROGRESS: {
    label: 'In progress',
    dotClass: 'bg-primary',
    terminal: false,
  },
  COMPLETED: {
    label: 'Completed',
    terminal: true,
    colorScheme: 'emerald',
    Icon: Check,
  },
  CLOSED: {
    label: 'Closed',
    terminal: true,
    colorScheme: 'emerald',
    Icon: Check,
  },
  ARCHIVED: {
    label: 'Archived',
    terminal: true,
    colorScheme: 'gray',
    Icon: Archive,
  },
};
const statusMetaFor = (row) =>
  STATUS[row?.assignment_details?.status] || STATUS.PENDING;

// The pill's hover label: a live deadline while the work is live, the date it
// finished once it isn't.
const swapLabelFor = (status, d) => {
  if (!status.terminal) return `Due ${formatDate(d.deadline)}`;
  if (!d.updated_at) return status.label;
  return `${status.label} ${formatDate(d.updated_at)}`;
};

// Fill + text colour per pill state — same tokens the CSE opportunity cards
// use for their due-date pill, so the two lists read consistently.
const PILL_STYLE = {
  red: { className: 'bg-ds-danger-subtle text-ds-danger', Icon: AlertCircle },
  amber: { className: 'bg-ds-warning-subtle text-ds-warning', Icon: ClockIcon },
  emerald: {
    className: 'bg-ds-success-subtle text-ds-success',
    // Was left unset — every other scheme here (red/amber/gray) carries a
    // real icon, so "On track" was the one urgency pill rendering as
    // bare text. Reuses the same Check the terminal Completed/Closed
    // pills already use for the same "good state" meaning.
    Icon: Check,
  },
  gray: { className: 'bg-muted text-muted-foreground', Icon: ClockIcon },
};

// The urgency/status pill: shows the urgency (or, once finished, the
// outcome) by default, and crossfades to the deadline (or close-out date) on
// hover/focus of the card — both labels share one grid cell so the pill is
// pre-sized to the wider of the two and nothing shifts.
const UrgencyPill = ({ status, d, urgencyScheme }) => {
  const style =
    PILL_STYLE[status.terminal ? status.colorScheme : urgencyScheme] ||
    PILL_STYLE.gray;
  const Icon = status.terminal ? status.Icon : style.Icon;
  const primaryLabel = status.terminal ? status.label : d.priority || 'N/A';
  const swapLabel = swapLabelFor(status, d);
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1.5 px-2.5 py-1 rounded-sm text-[11.5px] font-semibold whitespace-nowrap',
        style.className
      )}
    >
      {Icon && <Icon className="w-3 h-3 flex-shrink-0" strokeWidth={2} />}
      <span className="inline-grid">
        <span className="[grid-area:1/1] transition-opacity duration-150 group-hover:opacity-0 group-focus-within:opacity-0">
          {primaryLabel}
        </span>
        <span
          aria-hidden="true"
          className="[grid-area:1/1] opacity-0 transition-opacity duration-150 group-hover:opacity-100 group-focus-within:opacity-100"
        >
          {swapLabel}
        </span>
      </span>
      <span className="sr-only"> — {swapLabel}</span>
    </span>
  );
};

// Urgency badge — returns colorScheme name for the dark-mode-aware StatusPill.
// Real field/vocabulary confirmed live off GET /internal/assignments:
// assignment_details.priority is "Overdue" / "Upcoming" / "On track" — this
// used to read a `urgency` field (HIGH/MEDIUM/DUE_SOON/LOW/CRITICAL) that
// doesn't exist anywhere on the real response, which is why the pill always
// fell through to the 'N/A' default; see UrgencyPill's primaryLabel and
// every call site below, all switched from d.urgency to d.priority together.
// The old HIGH/MEDIUM/DUE_SOON/CRITICAL/LOW cases are kept too in case a
// future response ever sends that vocabulary instead — harmless dead
// branches, not a guess replacing a confirmed one.
const getUrgencyPillProps = (priority) => {
  const level = String(priority || '').toUpperCase();
  switch (level) {
    case 'OVERDUE':
    case 'CRITICAL':
      return { colorScheme: 'red' };
    case 'UPCOMING':
    case 'HIGH':
    case 'MEDIUM':
    case 'DUE_SOON':
      return { colorScheme: 'amber' };
    case 'ON TRACK':
    case 'LOW':
      return { colorScheme: 'emerald' };
    default:
      return { colorScheme: 'gray' };
  }
};

// The card's left accent bar only breaks from brand blue while the work is
// still live and genuinely urgent — a finished/archived assignment has no
// deadline pressure left, so it keeps the neutral brand accent.
const accentColorFor = (row) => {
  const status = statusMetaFor(row);
  if (!status.terminal) {
    const { colorScheme } = getUrgencyPillProps(
      row?.assignment_details?.priority
    );
    if (colorScheme === 'red') return 'var(--ds-danger)';
    if (colorScheme === 'amber') return 'var(--ds-warning)';
  }
  return 'hsl(var(--primary))';
};

const MetricCell = ({ value, label }) => {
  const empty = !value || value === 0;
  return (
    <div className="px-[16px] py-[7px] text-center min-w-[66px]">
      <span
        className={cn(
          'block font-mono text-[15px] leading-[1.1] tabular-nums font-bold',
          empty ? 'text-foreground-muted' : 'text-foreground-strong'
        )}
      >
        {value ?? 0}
      </span>
      <span className="block text-[10.5px] text-foreground-muted mt-[2px]">
        {label}
      </span>
    </div>
  );
};

const fillPct = (requested, submitted) =>
  requested ? Math.round(((submitted || 0) / requested) * 100) : null;

const FillCell = ({ requested, submitted }) => {
  const pct = fillPct(requested, submitted);
  if (pct == null) {
    return (
      <span className="text-[11.5px] font-medium text-foreground-muted">—</span>
    );
  }
  const w = Math.min(100, pct);
  return (
    <div className="flex items-center justify-end gap-2">
      <div className="w-[70px] h-[6px] bg-muted rounded-full overflow-hidden">
        <div
          className={cn(
            'h-full rounded-full',
            pct >= 100 ? 'bg-ds-success' : 'bg-primary'
          )}
          style={{ width: `${w}%` }}
        />
      </div>
      <span className="text-[11.5px] font-semibold font-mono text-muted-foreground min-w-[30px] text-right">
        {pct}%
      </span>
    </div>
  );
};

const LocationBreakdown = ({ row }) => {
  const d = row?.assignment_details || {};
  const locations = row?.locations || [];
  return (
    <div>
      <div className="grid grid-cols-[1.6fr_0.9fr_0.7fr_0.7fr_0.7fr_1fr] gap-x-3 px-3 py-2.5 border-b border-border text-[11px] font-[650] uppercase tracking-[0.06em] text-muted-foreground">
        <span>Location</span>
        <span>Employment type</span>
        <span className="text-right">Requested</span>
        <span className="text-right">Submitted</span>
        <span className="text-right">Passed</span>
        <span className="text-right">Fill rate</span>
      </div>
      <div className="divide-y divide-border">
        {locations.map((loc) => (
          <div
            key={loc?.mapping_id || loc?.requirement_location_id}
            className="grid grid-cols-[1.6fr_0.9fr_0.7fr_0.7fr_0.7fr_1fr] gap-x-3 px-3 py-2.5 text-[13px] items-center"
          >
            <span className="font-medium flex items-center gap-2 text-foreground-strong">
              <MapPin className="w-3.5 h-3.5 text-muted-foreground flex-shrink-0" />
              {loc?.location_name || 'N/A'}
            </span>
            <span className="text-muted-foreground">
              {loc?.employment_type || 'N/A'}
            </span>
            <span
              className="text-right font-medium text-foreground-strong"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {loc?.candidates_requested ?? 0}
            </span>
            <span
              className="text-right font-medium text-foreground-strong"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {loc?.candidates_submitted ?? 0}
            </span>
            <span
              className="text-right font-medium text-foreground-strong"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {loc?.candidates_passed ?? 0}
            </span>
            <FillCell
              requested={loc?.candidates_requested}
              submitted={loc?.candidates_submitted}
            />
          </div>
        ))}
        {!locations.length && (
          <div className="px-3 py-4 text-[13px] text-center text-muted-foreground">
            No location data available.
          </div>
        )}
        {locations.length > 1 && (
          <div className="grid grid-cols-[1.6fr_0.9fr_0.7fr_0.7fr_0.7fr_1fr] gap-x-3 px-3 py-2.5 text-[13px] items-center border-t border-border font-semibold">
            <span className="text-foreground-strong">Total</span>
            <span className="text-muted-foreground font-normal">
              {locations.length} locations
            </span>
            <span
              className="text-right text-foreground-strong"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {d.candidates_requested ?? 0}
            </span>
            <span
              className="text-right text-foreground-strong"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {d.candidates_submitted ?? 0}
            </span>
            <span
              className="text-right text-foreground-strong"
              style={{ fontVariantNumeric: 'tabular-nums' }}
            >
              {d.candidates_passed ?? 0}
            </span>
            <FillCell
              requested={d.candidates_requested}
              submitted={d.candidates_submitted}
            />
          </div>
        )}
      </div>
    </div>
  );
};

// The panel carries only what the summary row does NOT already say: the
// location break-up, plus — for finished work — the date it closed (which no
// longer has a deadline pill to live in) and its publish state.
const AssignmentDetail = ({ row }) => {
  const navigate = useNavigate();
  const d = row?.assignment_details || {};
  const status = statusMetaFor(row);

  const handleSubmitCandidates = () => {
    const p = new URLSearchParams({ assignmentId: row?.id });
    if (row?.requirement?.requirement_id)
      p.set('opportunityId', row.requirement.requirement_id);
    const rlId = row?.locations?.[0]?.requirement_location_id;
    if (rlId) p.set('requirementLocationId', rlId);
    navigate(`/candidates/submit?${p.toString()}`);
  };

  // This view is TA-only (see the page heading/"My assignments" copy
  // below) — /candidates/list is RequirementPipeline.jsx, the CSE-only submitted-
  // candidates view, and isn't meaningful for a TA. The equivalent TA page
  // is /assignment-pipeline (same fix already applied to SubmitCandidate.jsx's
  // own "View Submitted" button — see that file for why status stays
  // lowercase 'new' here: AssignmentPipeline.jsx's tab-matching effect
  // falls back to the first tab by order_number when it doesn't literally
  // match a real tab value, which for this flow is "New" anyway).
  const handleViewSubmission = () => {
    console.log(
      'handleViewSubmission',
      row?.requirement?.requirement_id,
      row?.locations?.[0]?.requirement_location_id,
      row?.id
    );
    const p = new URLSearchParams({ status: 'new' });
    if (row?.requirement?.requirement_id)
      p.set('opportunityId', row.requirement.requirement_id);
    const rlId = row?.locations?.[0]?.requirement_location_id;
    if (rlId) p.set('requirementLocationId', rlId);
    if (row?.id) p.set('assignmentId', row.id);
    navigate(`/assignment-pipeline?${p.toString()}`);
  };

  return (
    <div
      className="border-t border-border px-[18px] py-[14px]"
      style={{ backgroundColor: 'var(--muted)' }}
    >
      <p className="text-[10.5px] font-semibold uppercase tracking-[0.1em] text-muted-foreground mb-2.5">
        Location break-up
      </p>
      <LocationBreakdown row={row} />

      <div className="flex items-center flex-wrap gap-x-4 gap-y-2 mt-3 text-[12.5px] text-muted-foreground">
        {status.terminal && d.updated_at && (
          <span className="inline-flex items-center gap-1.5">
            <span
              className={cn(
                'w-[7px] h-[7px] rounded-full',
                status.colorScheme === 'gray'
                  ? 'bg-muted-foreground'
                  : 'bg-ds-success'
              )}
            />
            {status.label} {formatDate(d.updated_at)}
          </span>
        )}

        <span className="ml-auto inline-flex items-center gap-3.5">
          {!status.terminal && (
            <Button
              size="sm"
              variant="outline"
              className="h-auto px-3.5 py-[7px] rounded-[7px] text-[12px] font-[550] gap-1.5"
              onClick={(e) => {
                e.stopPropagation();
                handleSubmitCandidates();
              }}
            >
              <UserPlus className="w-3.5 h-3.5" strokeWidth={1.8} />
              Submit candidates
            </Button>
          )}
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleViewSubmission();
            }}
            className="inline-flex items-center gap-1.5 text-[12.5px] font-semibold text-primary hover:text-brand-deep transition-colors"
          >
            <FileText className="w-3.5 h-3.5" />
            View submission
          </button>
        </span>
      </div>
    </div>
  );
};

const AssignmentCard = ({
  row,
  navigate,
  toast,
  expanded,
  onToggleExpand,
  isArchiveTab,
}) => {
  const d = row?.assignment_details || {};
  const status = statusMetaFor(row);
  const accent = accentColorFor(row);
  const { colorScheme: urgencyScheme } = getUrgencyPillProps(d.priority);
  const locations = row?.locations || [];

  const subtitle = (() => {
    const locPart =
      locations.length === 1
        ? locations[0]?.location_name
        : `${locations.length} locations`;
    const types = Array.from(
      new Set(locations.map((l) => l.employment_type).filter(Boolean))
    );
    const typePart = types.length <= 2 ? types.join(' & ') : 'Mixed';
    return [locPart, typePart].filter(Boolean).join(' · ');
  })();

  const handleCopyId = async (e) => {
    e.stopPropagation();
    try {
      await navigator.clipboard.writeText(
        String(row?.requirement?.registration_id ?? '')
      );
      toast({
        message: 'Registration ID copied',
        variant: 'success',
        duration: 1500,
      });
    } catch {
      toast({ message: 'Copy failed', variant: 'destructive', duration: 1500 });
    }
  };

  const handleCardClick = () => {
    const p = new URLSearchParams({ assignmentId: row?.id });
    const rlId = locations?.[0]?.requirement_location_id;
    if (rlId) p.set('requirementLocationId', rlId);
    if (d.candidates_requested != null)
      p.set('candidateRequested', d.candidates_requested);
    navigate(
      `/opportunity/${row?.requirement?.requirement_id}?${p.toString()}`
    );
  };
  const handleCardKeyDown = (e) => {
    if (e.key === 'Enter' || e.key === ' ') handleCardClick();
  };

  return (
    <div className="relative overflow-hidden bg-card border border-border rounded-[10px]">
      <span
        className="absolute left-0 top-0 bottom-0 w-[3px]"
        style={{ backgroundColor: accent }}
        aria-hidden="true"
      />
      {d.is_published && (
        <div
          className="absolute shadow-lg top-0 left-5 bottom-0 py-[2px] px-[5px] h-fit text-[8px] font-semibold text-white bg-emerald-600 origin-top-left rounded-b-sm"
          aria-hidden="true"
        >
          <Check className="w-2.5 h-2.5 inline-block mr-[2px] -translate-y-[1px]" />
          published
        </div>
      )}
      <div
        role="button"
        tabIndex={0}
        className="flex items-center gap-[18px] px-[18px] py-[13px] pl-[24px] cursor-pointer group"
        onClick={handleCardClick}
        onKeyDown={handleCardKeyDown}
      >
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-[14.5px] font-[650] text-foreground-strong tracking-[-0.01em] leading-snug">
              {row?.requirement?.title}
            </h3>
            {row?.current_cse_id === 0 && (
              <span className="text-[9.5px] font-semibold uppercase tracking-[0.03em] px-[6px] py-[1px] rounded-[4px] bg-brand-bg-subtle text-primary">
                NEW
              </span>
            )}
          </div>

          <p className="text-[12px] text-foreground-muted mt-[2px]">
            {subtitle}
          </p>

          <div className="flex items-center gap-[7px] mt-[7px] text-[11.5px] flex-wrap">
            {!status.terminal && (
              <>
                <span className="inline-flex items-center gap-[5px] font-medium text-muted-foreground">
                  <span
                    className={cn(
                      'w-[6px] h-[6px] rounded-full',
                      status.dotClass
                    )}
                  />
                  {status.label}
                </span>
                <span
                  className="w-[3px] h-[3px] rounded-full bg-border"
                  aria-hidden="true"
                />
              </>
            )}
            <span className="flex items-center gap-[5px]">
              <span className="font-mono font-semibold text-muted-foreground">
                {row?.requirement?.registration_id}
              </span>
              <button
                type="button"
                onClick={handleCopyId}
                aria-label="Copy registration ID"
                className="opacity-0 group-hover:opacity-100 transition-opacity rounded p-0.5 text-foreground-muted hover:text-primary"
              >
                <Copy className="w-[12px] h-[12px]" />
              </button>
            </span>
            <span
              className="w-[3px] h-[3px] rounded-full bg-border"
              aria-hidden="true"
            />
            <span className="text-muted-foreground font-medium">
              {row?.organization?.name}
            </span>
            <span
              className="w-[3px] h-[3px] rounded-full bg-border"
              aria-hidden="true"
            />
            <span className="text-foreground-muted">
              Assigned {formatDate(d.created_at)}
            </span>
            {d.assigned_from_name && (
              <>
                <span
                  className="w-[3px] h-[3px] rounded-full bg-border"
                  aria-hidden="true"
                />
                <span className="text-foreground-muted">
                  from {d.assigned_from_name}
                </span>
              </>
            )}
          </div>
        </div>

        <div className="flex flex-col items-end gap-[8px] flex-shrink-0">
          <div className="flex items-center gap-1.5">
            {/* Per explicit request: an archived assignment's deadline
                pressure is moot, so the raw Overdue/Upcoming/On track
                pill is suppressed here — unlike the CSE list, this one
                still shows a genuine terminal-status outcome pill
                (Completed/Closed/Archived) when that applies, since that's
                an outcome, not a deadline warning. isArchiveTab reflects
                the Archive TAB (a separate query flag, not necessarily
                assignment_details.status === 'ARCHIVED'), so a still-
                PENDING row surfaced there is exactly the case this guards. */}
            {(!isArchiveTab || status.terminal) && (
              <UrgencyPill
                status={status}
                d={d}
                urgencyScheme={urgencyScheme}
              />
            )}
          </div>

          <div className="flex border border-borderColorSecondary rounded-[9px] overflow-hidden divide-x divide-borderColorSecondary">
            <MetricCell value={d.candidates_requested} label="Requested" />
            <MetricCell value={d.candidates_submitted} label="Submitted" />
            <MetricCell value={d.candidates_passed} label="Passed" />
          </div>
        </div>

        <Button
          variant="ghost"
          size="sm"
          onClick={(e) => {
            e.stopPropagation();
            onToggleExpand(row?.id);
          }}
          aria-expanded={expanded}
          aria-label={`Location breakdown for ${row?.requirement?.title || 'assignment'}`}
          className="h-8 w-8 p-0 text-muted-foreground hover:text-muted-foreground hover:bg-muted flex-shrink-0"
        >
          <ChevronDown
            className={cn(
              'h-4 w-4 transition-transform',
              expanded && 'rotate-180'
            )}
          />
        </Button>
      </div>

      {expanded && <AssignmentDetail row={row} />}
    </div>
  );
};

const AssignmentListSkeleton = () => (
  <div className="flex flex-col gap-[10px]">
    {['sk-0', 'sk-1', 'sk-2', 'sk-3', 'sk-4'].map((id) => (
      <div
        key={id}
        className="relative bg-card border border-border rounded-[10px] px-[18px] py-[13px] pl-[24px] flex items-center gap-[18px] animate-pulse"
      >
        <span className="absolute left-[10px] top-[14px] bottom-[14px] w-[3px] rounded-[3px] bg-border" />
        <div className="flex-1 min-w-0 space-y-[8px]">
          <div className="h-[18px] bg-muted rounded-[4px] w-2/3" />
          <div className="h-[13px] bg-muted rounded-[4px] w-1/3" />
          <div className="h-[13px] bg-muted rounded-[4px] w-1/4" />
        </div>
        <div className="flex flex-col items-end gap-[8px]">
          <div className="h-[24px] bg-muted rounded-[6px] w-[80px]" />
          <div className="h-[42px] bg-muted rounded-[9px] w-[198px]" />
        </div>
      </div>
    ))}
  </div>
);

const AssignmentEmptyState = ({ searchQuery, activeTab }) => (
  <div className="border border-border rounded-xl bg-card">
    <div className="flex flex-col items-center justify-center py-[50px] px-5 text-center">
      <div className="w-[46px] h-[46px] rounded-xl bg-brand-bg-subtle text-primary inline-flex items-center justify-center mb-3">
        <FileText className="w-[22px] h-[22px]" strokeWidth={1.8} />
      </div>
      <p className="text-[15px] font-[650] text-foreground-strong mb-1">
        No assignments found
      </p>
      <p className="text-[13px] text-foreground-muted max-w-[320px]">
        {searchQuery
          ? 'No assignments match your search.'
          : `There are no ${activeTab} assignments right now.`}
      </p>
    </div>
  </div>
);

// Friendly label for the role badge in the page meta row — the auth payload
// only carries the raw role slug.
const ROLE_LABEL = {
  ahedge_ta: 'Talent acquisition',
};

function TaAssignmentView({
  taData,
  isLoading,
  currentPage,
  currentPageSize,
  onPageChange,
  onPageSizeChange,
  taSummaryData,
  activeTab,
  onTabChange,
  mockFilter,
  onMockFilterChange,
  searchQuery,
  onSearchChange,
  dateRange,
  onDateRangeChange,
}) {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { getUserInfo } = useAuth();
  const [expandedId, setExpandedId] = useState(null);

  const user = getUserInfo();
  console.log('TaAssignmentView user', user);
  const TA_CODE = `TA-00${user?.id || '000'}`;
  const roleLabel =
    ROLE_LABEL[user?.roles?.find((r) => ROLE_LABEL[r])] || 'Talent acquisition';

  const toggleExpand = (id) => {
    if (id === undefined || id === null) return;
    setExpandedId((prev) => (prev === id ? null : id));
  };

  // The TA assignments endpoint is being migrated to the same envelope
  // CSE's requirements list already uses — `data.items[]` (still the same
  // assignment-shaped objects AssignmentCard already renders, just wrapped)
  // plus a real `data.pagination` — but isn't live yet, so this still has
  // to handle today's shape too: `data` as a bare array with no pagination
  // info anywhere. Once the backend ships the new envelope this starts
  // reading real `total`/`total_pages` automatically, with no further
  // frontend change needed; until then, `total`/`total_pages` are computed
  // from the current page's own item count (the best available signal with
  // no real total), which can never exceed one page's worth — it's a stand
  // -in for "pagination always looks like just one page", not a correct
  // total, but it's honest about what's actually knowable right now.
  const rawTaData = taData?.data;
  const assignments = Array.isArray(rawTaData)
    ? rawTaData
    : rawTaData?.items || [];
  const paginationMeta = Array.isArray(rawTaData)
    ? null
    : rawTaData?.pagination;
  const totalItems = paginationMeta?.total ?? assignments.length;
  const totalPages =
    paginationMeta?.total_pages ??
    Math.max(1, Math.ceil(assignments.length / currentPageSize));

  // Same tab count-badge treatment the CSE Opportunities tabs already use
  // (Opportunities.jsx's own apiMetrics/getTabCount) — reads off this same
  // new envelope's `metrics`, absent entirely until the backend ships it,
  // in which case every tab just renders without a badge (TaFilterBar's
  // own `count != null` guard). The one metric key that doesn't match its
  // tab key 1:1 is Archive — the sample payload calls it `archived`, same
  // mismatch CSE's own TAB_METRIC_KEY already has to account for.
  const apiMetrics = (!Array.isArray(rawTaData) && rawTaData?.metrics) || {};
  const tabCounts = {
    active: apiMetrics.active,
    archive: apiMetrics.archived,
  };

  const overview = taSummaryData?.data?.overview;
  const requested = overview?.total_open_pool_requested ?? 0;
  const submitted = overview?.total_candidates_submitted ?? 0;
  const submittedPct = requested
    ? Math.round((submitted / requested) * 100)
    : 0;
  const customers = overview?.total_customer_count ?? 0;
  const overdue = overview?.total_overdue ?? 0;
  const dueSoon = overview?.total_due ?? 0;
  const activeAssignments = overview?.total_due ?? 0;

  return (
    <PageContainer className="space-y-4">
      {/* Page heading */}
      <div>
        <h1 className="text-[22px] font-bold tracking-[-0.04em] text-foreground-strong m-0 mb-1">
          My assignments
        </h1>
        <p className="text-[13.5px] text-muted-foreground m-0 leading-[1.55] max-w-[64ch]">
          Requirements the customer service team has forwarded to you — with the
          location-wise break-up of openings inside each one.
        </p>
        <div className="flex items-center flex-wrap gap-2 mt-2.5 text-[12px] text-muted-foreground">
          <span className="font-mono text-[11.5px] font-semibold text-primary bg-brand-bg-subtle border border-primary/15 rounded-md px-2 py-0.5">
            {TA_CODE}
          </span>
          <span>{user?.fullName}</span>
          <span
            className="w-[3px] h-[3px] rounded-full bg-border"
            aria-hidden="true"
          />
          <span>{roleLabel}</span>
        </div>
      </div>

      {/* Summary metric strip (§18) */}
      <div className="grid grid-cols-5 gap-[14px]">
        <MetricTile
          icon={Inbox}
          iconBg="hsl(var(--brand-bg-subtle))"
          iconColor="hsl(var(--primary))"
          value={activeAssignments}
          label="Active assignments"
          sub={`Across ${customers} customer${customers === 1 ? '' : 's'}`}
        />
        <MetricTile
          icon={AlertCircle}
          iconBg="var(--ds-danger-subtle)"
          iconColor="var(--ds-danger)"
          value={overdue}
          label="Overdue"
        />
        <MetricTile
          icon={ClockIcon}
          iconBg="var(--ds-warning-subtle)"
          iconColor="var(--ds-warning)"
          value={dueSoon}
          label="Due soon"
        />
        <MetricTile
          wide
          icon={CheckSquare}
          iconBg="var(--ds-success-subtle)"
          iconColor="var(--ds-success)"
          value={submitted}
          ofValue={requested}
          label="Candidates submitted"
          sub={`${submittedPct}% of requested`}
          barPct={submittedPct}
        />
      </div>

      {/* Main content card */}
      {/* Filter Bar */}
      <TaFilterBar
        searchQuery={searchQuery || ''}
        onSearchChange={onSearchChange}
        activeTab={activeTab}
        onTabChange={onTabChange}
        mockFilter={mockFilter}
        onMockFilterChange={onMockFilterChange}
        searchPlaceholder="Search role, Reg ID..."
        tabCounts={tabCounts}
        dateRange={dateRange}
        onDateRangeChange={onDateRangeChange}
      />

      {/* Assignment card list */}
      {isLoading && <AssignmentListSkeleton />}
      {!isLoading && assignments.length === 0 && (
        <AssignmentEmptyState searchQuery={searchQuery} activeTab={activeTab} />
      )}
      {!isLoading && assignments.length > 0 && (
        <div className="flex flex-col gap-[10px]">
          {assignments.map((row) => (
            <AssignmentCard
              key={row?.id}
              row={row}
              navigate={navigate}
              toast={toast}
              expanded={expandedId === row?.id}
              onToggleExpand={toggleExpand}
              isArchiveTab={activeTab === 'archive'}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {!isLoading && assignments.length > 0 && (
        <TablePagination
          currentPage={currentPage}
          totalPages={totalPages}
          pageSize={currentPageSize}
          totalItems={totalItems}
          onPageChange={onPageChange}
          onPageSizeChange={onPageSizeChange}
          itemLabel="assignments"
        />
      )}
    </PageContainer>
  );
}

export default TaAssignmentView;
